"use client"

import type React from "react"

import { useEffect, useMemo, useState } from "react"
import Link from "next/link"
import { useRouter, useSearchParams } from "next/navigation"
import { useTranslation } from "react-i18next"
import { FcGoogle } from "react-icons/fc"
import { toast } from "sonner"
import { CheckCircle, ChevronLeft, Eye, EyeOff, Loader2, LogIn } from "lucide-react"

import { azureDb } from "@/lib/azure/compat-client"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Separator } from "@/components/ui/separator"
import { getOAuthCallbackUrl } from "@/lib/app-url"
import { OTP_CODE_LENGTH } from "@/lib/azure/compat-client"
import { LegalNotice } from "@/components/legal/legal-notice"

type LoginNamespace = "auth-login" | "applicant-login"

interface RoleLoginPageProps {
  namespace: LoginNamespace
  signupHref: string
  forgotPasswordHref: string
  secondaryLink?: {
    href: string
    label: string
    prompt: string
    className?: string
  }
}

export function RoleLoginPage({
  namespace,
  signupHref,
  forgotPasswordHref,
  secondaryLink,
}: RoleLoginPageProps) {
  const otpLength = OTP_CODE_LENGTH
  const router = useRouter()
  const searchParams = useSearchParams()
  const [isLoading, setIsLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState("")
  const [message, setMessage] = useState("")
  const [showOtpModal, setShowOtpModal] = useState(false)
  const [otpCode, setOtpCode] = useState("")
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false)
  const [isResendingOtp, setIsResendingOtp] = useState(false)

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    rememberMe: false,
  })

  const { t, i18n } = useTranslation(namespace)
  const googleEnabled = namespace === "applicant-login"

  const redirectTarget = useMemo(() => {
    const redirect = searchParams.get("redirect")
    if (!redirect || !redirect.startsWith("/")) {
      return null
    }
    return redirect
  }, [searchParams])

  useEffect(() => {
    const messageParam = searchParams.get("message")
    const authError = searchParams.get("auth_error")
    if (messageParam) {
      setMessage(messageParam)
    }
    if (authError) {
      setError(authError)
    }
  }, [searchParams])

  const resolveUserType = (metadata: Record<string, any> | undefined) => {
    const metaType = metadata?.user_type as string | undefined

    if (metaType === "company") {
      return {
        userType: "company" as const,
        displayName: metadata?.company_name || "",
        fallbackPath: "/employer/jobs",
      }
    }

    if (metaType === "job_seeker") {
      return {
        userType: "job_seeker" as const,
        displayName: metadata?.full_name || "",
        fallbackPath: "/applicant/dashboard",
      }
    }

    return null
  }

  const handlePostAuthRedirect = async (metadata: Record<string, any> | undefined) => {
    const result = resolveUserType(metadata)

    if (!result) {
      setError(t("profileNotFound") || "No account profile found. Please sign up first.")
      await azureDb.auth.signOut()
      return
    }

    if (result.userType === "company") {
      toast.success(`${t("welcomeBackCompany") || "Welcome back"}, ${result.displayName || "Employer"}!`)
    } else {
      toast.success(`${t("welcomeBack") || "Welcome back"}, ${result.displayName || ""}!`)
    }

    router.replace(redirectTarget || result.fallbackPath)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError("")
    setIsLoading(true)

    try {
      const { data, error: authError } = await azureDb.auth.signInWithPassword({
        email: formData.email,
        password: formData.password,
      })

      if (authError) {
        if (authError.message.includes("Invalid login credentials")) {
          setError(t("invalidCredentials") || t("invalidEmailPassword"))
        } else if (authError.message.includes("Email not confirmed")) {
          await handleResendOtp()
        } else if (authError.message.includes("Too many requests")) {
          setError(t("tooManyRequests"))
        } else {
          setError(authError.message)
        }
        return
      }

      if (!data.user) {
        setError(t("loginFailed"))
        return
      }

      await handlePostAuthRedirect(data.user.user_metadata)
    } catch (loginError) {
      console.error("Login error:", loginError)
      setError("An unexpected error occurred. Please try again.")
    } finally {
      setIsLoading(false)
    }
  }

  const handleResendOtp = async () => {
    setIsResendingOtp(true)
    setError("")

    try {
      const { error: resendError } = await azureDb.auth.resend({
        type: "signup",
        email: formData.email,
      })

      if (resendError) {
        if (resendError.message.includes("rate limit")) {
          setError(t("tooManyRequests"))
        } else {
          setError(resendError.message)
        }
        return
      }

      setShowOtpModal(true)
      toast.success(t("otpSent") || "Verification code sent! Please check your email.")
    } catch (resendError) {
      console.error("Resend OTP error:", resendError)
      setError("Failed to send verification code. Please try again.")
    } finally {
      setIsResendingOtp(false)
    }
  }

  const handleOtpVerification = async () => {
    if (otpCode.length !== otpLength) {
      setError(t("invalidOtp") || "Please enter a valid 6-digit verification code")
      return
    }

    setIsVerifyingOtp(true)
    setError("")

    try {
      const { data: verifyData, error: verifyError } = await azureDb.auth.verifyOtp({
        email: formData.email,
        token: otpCode,
        type: "email",
      })

      if (verifyError) {
        setError(`${t("verificationFailed") || "Verification failed"}: ${verifyError.message}`)
        return
      }

      if (!verifyData.user) {
        setError(t("verificationFailed") || "Verification failed - no user data")
        return
      }

      setShowOtpModal(false)
      setOtpCode("")
      toast.success(t("emailVerified") || "Email verified successfully!")

      const { data, error: signInError } = await azureDb.auth.signInWithPassword({
        email: formData.email,
        password: formData.password,
      })

      if (signInError) {
        setError(signInError.message)
        return
      }

      if (!data.user) {
        setError(t("loginFailed"))
        return
      }

      await handlePostAuthRedirect(data.user.user_metadata)
    } catch (verificationError) {
      console.error("OTP verification error:", verificationError)
      setError("An unexpected error occurred during verification")
    } finally {
      setIsVerifyingOtp(false)
    }
  }

  const handleForgotPassword = () => {
    const emailParam = formData.email ? `?email=${encodeURIComponent(formData.email)}` : ""
    router.push(`${forgotPasswordHref}${emailParam}`)
  }

  const handleGoogleSignIn = async () => {
    if (!googleEnabled) {
      return
    }

    setError("")
    document.cookie = "google_auth_intent=job_seeker; path=/; max-age=600; SameSite=Lax"

    const callbackUrl = new URL(getOAuthCallbackUrl(window.location.origin))
    if (redirectTarget) {
      callbackUrl.searchParams.set("next", redirectTarget)
    }

    const { error: oauthError } = await azureDb.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: callbackUrl.toString(),
      },
    })

    if (oauthError) {
      setError(oauthError.message)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50" dir={i18n.language === "ar" ? "rtl" : "ltr"}>
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center space-x-2 text-sm text-gray-600 mb-6 rtl:space-x-reverse">
          <Link href="/" className="hover:text-blue-600">
            {t("home")}
          </Link>
          <span>{">"}</span>
          <span className="text-gray-900">{t("signIn")}</span>
        </div>

        <div className="max-w-md mx-auto">
          <div className="flex items-center space-x-3 mb-8 rtl:space-x-reverse">
            <Link href="/">
              <Button variant="ghost" size="icon">
                <ChevronLeft className={`w-5 h-5 ${i18n.language === "ar" ? "rotate-180" : ""}`} />
              </Button>
            </Link>
            <h1 className="text-3xl font-bold text-gray-900">{t("signIn")}</h1>
          </div>

          <Card>
            <CardHeader className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <LogIn className="w-8 h-8 text-blue-600" />
              </div>
              <CardTitle>{t("signInAccount") || t("welcomeBack") || "Welcome Back"}</CardTitle>
              <CardDescription>{t("signInToAccount") || t("accessDashboard") || "Sign in to your account"}</CardDescription>
            </CardHeader>
            <CardContent>
              {message && (
                <Alert className="mb-6 border-blue-200 bg-blue-50">
                  <AlertDescription className="text-blue-800">{message}</AlertDescription>
                </Alert>
              )}

              {error && (
                <Alert className="mb-6 border-red-200 bg-red-50">
                  <AlertDescription className="text-red-800">{error}</AlertDescription>
                </Alert>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="email">{t("emailAddress") || t("email") || "Email"}</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="your@email.com"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">{t("password") || "Password"}</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      required
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder={t("passwordPlaceholder") || "Enter your password"}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2 rtl:space-x-reverse">
                    <Checkbox
                      id="remember"
                      checked={formData.rememberMe}
                      onCheckedChange={(checked) => setFormData({ ...formData, rememberMe: checked as boolean })}
                    />
                    <Label htmlFor="remember" className="text-sm">
                      {t("rememberMe") || "Remember me"}
                    </Label>
                  </div>
                  <Button
                    type="button"
                    variant="link"
                    className="text-sm text-blue-600 hover:text-blue-800 p-0"
                    onClick={handleForgotPassword}
                    disabled={isLoading}
                  >
                    {t("forgotPassword") || "Forgot password?"}
                  </Button>
                </div>

                <Button type="submit" className="w-full" size="lg" disabled={isLoading}>
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t("signingIn") || "Signing in..."}
                    </>
                  ) : (
                    <>
                      <LogIn className="w-4 h-4 mr-2" />
                      {t("signIn") || "Sign In"}
                    </>
                  )}
                </Button>
              </form>

              {googleEnabled && (
                <>
                  <div className="relative my-6">
                    <div className="absolute inset-0 flex items-center">
                      <Separator className="w-full" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-white px-2 text-gray-500">{t("orContinueWith") || "Or continue with"}</span>
                    </div>
                  </div>

                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    size="lg"
                    onClick={handleGoogleSignIn}
                    disabled={isLoading}
                  >
                    <FcGoogle className="w-5 h-5 mr-2" />
                    {t("signInWithGoogle") || "Sign in with Google"}
                  </Button>
                </>
              )}

              <LegalNotice kind="pilot" />

              <Separator className="my-6" />

              <div className="text-center space-y-2">
                <p className="text-sm text-gray-600">
                  {t("noAccount") || "Don't have an account?"}{" "}
                  <Link href={signupHref} className="text-blue-600 hover:text-blue-800 font-medium">
                    {t("createOneHere") || t("createAccount") || "Create one here"}
                  </Link>
                </p>
                {secondaryLink && (
                  <p className="text-sm text-gray-500">
                    {secondaryLink.prompt}{" "}
                    <Link href={secondaryLink.href} className={secondaryLink.className || "text-green-600 hover:text-green-800 font-medium"}>
                      {secondaryLink.label}
                    </Link>
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <Dialog open={showOtpModal} onOpenChange={setShowOtpModal}>
        <DialogContent className="sm:max-w-md" dir={i18n.language === "ar" ? "rtl" : "ltr"}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              {t("verifyEmail")}
            </DialogTitle>
            <DialogDescription>{t("otpSentDescription")}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {error && (
              <Alert className="border-red-200 bg-red-50">
                <AlertDescription className="text-red-800">{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="otp">{t("verificationCode") || "Verification Code"}</Label>
              <Input
                id="otp"
                type="text"
                maxLength={otpLength}
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, otpLength))}
                placeholder={"0".repeat(otpLength)}
                className="text-center text-2xl tracking-widest"
              />
            </div>

            <Button
              onClick={handleOtpVerification}
              className="w-full"
              disabled={isVerifyingOtp || otpCode.length !== otpLength}
            >
              {isVerifyingOtp ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t("verifying") || "Verifying..."}
                </>
              ) : (
                t("verifyAndSignIn") || "Verify & Sign In"
              )}
            </Button>

            <div className="text-center">
              <Button
                type="button"
                variant="link"
                className="text-sm text-blue-600 hover:text-blue-800"
                onClick={handleResendOtp}
                disabled={isResendingOtp}
              >
                {isResendingOtp ? (
                  <>
                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    {t("resending") || "Resending..."}
                  </>
                ) : (
                  t("resendCode") || "Didn't receive the code? Resend"
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
